import {
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChildren,
  effect,
  inject,
  input,
} from '@angular/core';
import { NgpTabButton, NgpTabList, NgpTabset } from 'ng-primitives/tabs';
import { LabeledOption, normalizeLabeledOptions } from '../shared/labeled-option';
import { HhTabPanelComponent } from './hh-tab-panel';

export type TabItem = LabeledOption;

/**
 * A pill tab switcher. Renders the tab strip only — panels are separate
 * `hh-tab-panel` children, projected below it, that read their active
 * state from the same tabset via DI. That split (rather than this
 * component owning panel content, the way hh-accordion owns its one
 * content block) is what lets a panel hold arbitrary markup instead of
 * a single string.
 *
 * `NgpTabset` is applied as a hostDirective rather than on a `<div>`
 * inside this component's own template. Angular's content-projection DI
 * only resolves providers from a directive's *declaring* component tree
 * — a directive on an inner template node is invisible to `<ng-content>`
 * children, even though they end up nested inside it in the rendered
 * DOM (confirmed by a real NG0201 "no provider" failure when this was
 * first tried as an inner div). The host element itself, though, is a
 * genuine DOM/injector ancestor of both this component's own template
 * output and anything projected into it, so hoisting the directive
 * there is what makes `hh-tab-panel` resolvable at all.
 *
 * That hoist has a second benefit: NgpTabset's `value` input/output
 * become this component's own `activeTab`/`activeTabChange`, so
 * `[(activeTab)]="mySignal"` works with no proxying on this end. It
 * does NOT, however, seed `activeTab` with a starting value on its own
 * — NgpTabset's fallback to the first non-disabled tab only feeds its
 * internal `selectedTab` (what gets rendered as active); nothing writes
 * that fallback back into `value` itself, so a caller's own bound
 * signal would stay `undefined` until a real click. The constructor
 * effect below closes that gap explicitly.
 */
@Component({
  selector: 'hh-tabs',
  standalone: true,
  hostDirectives: [
    {
      directive: NgpTabset,
      inputs: ['ngpTabsetValue: activeTab'],
      outputs: ['ngpTabsetValueChange: activeTabChange'],
    },
  ],
  imports: [NgpTabList, NgpTabButton],
  host: { class: 'hh-tabs' },
  template: `
    <div class="hh-tabs__list" ngpTabList>
      @for (tab of normalizedTabs(); track tab.value) {
        <button
          type="button"
          class="hh-tabs__tab"
          ngpTabButton
          [ngpTabButtonValue]="tab.value"
          [ngpTabButtonDisabled]="tab.disabled"
        >
          {{ tab.label }}
        </button>
      }
    </div>
    <ng-content />
  `,
  styleUrl: './hh-tabs.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HhTabsComponent {
  private readonly tabset = inject(NgpTabset);

  readonly tabs = input<(TabItem | string)[]>([]);

  /** input() has no custom-equal option (unlike computed()/signal()), so
   * a caller passing an inline array literal — a fresh reference every
   * change-detection pass — always re-runs this map. The equal option
   * here at least stops that from propagating further: downstream
   * reads (the @for below, the effects below) only see a change when
   * the normalized content actually differs. */
  protected readonly normalizedTabs = computed<TabItem[]>(
    () => normalizeLabeledOptions(this.tabs()),
    { equal: (a, b) => JSON.stringify(a) === JSON.stringify(b) },
  );

  private readonly panels = contentChildren(HhTabPanelComponent);

  constructor() {
    effect(() => {
      const first = this.normalizedTabs().find((tab) => !tab.disabled);
      if (first && this.tabset.value() === undefined) {
        this.tabset.select(first.value);
      }
    });

    // A hh-tab-panel whose value matches no tab hides itself permanently
    // (data-active never becomes true) with no error anywhere else in
    // the stack — this is the only place with visibility into both
    // lists, so it's the only place that can catch the mismatch.
    effect(() => {
      const validValues = new Set(this.normalizedTabs().map((tab) => tab.value));
      for (const panel of this.panels()) {
        if (!validValues.has(panel.value())) {
          console.warn(
            `hh-tabs: no tab with value "${panel.value()}" — this hh-tab-panel will never become active.`,
          );
        }
      }
    });
  }
}
